// ============================================================================
// Entity: CustomProperties
// Key: customProperties
// Kind: entity
// Generated file. Do not edit manually.
// ============================================================================

import {
	customPropertiesClientPost,
	customPropertiesCreate,
	customPropertiesDelete,
	customPropertiesRelationsRuleSetsAssign,
	customPropertiesRelationsRuleSetsUnassign,
	customPropertiesUpdate,
	getCustomPropertiesClientGetQueryKey,
	getCustomPropertiesClientGetQueryOptions,
	getCustomPropertiesDetailQueryKey,
	getCustomPropertiesDetailQueryOptions,
	getCustomPropertiesListQueryKey,
	getCustomPropertiesListQueryOptions,
	getCustomPropertiesRelationsRuleSetsAssignedQueryKey,
	getCustomPropertiesRelationsRuleSetsAssignedQueryOptions,
	getCustomPropertiesRelationsRuleSetsAvailableQueryKey,
	getCustomPropertiesRelationsRuleSetsAvailableQueryOptions,
	getRuleSetsListQueryKey,
} from '../api/backstage'
// #region Imports
import type {
	CreateCustomPropertiesRequest,
	CreateCustomPropertyRequest,
	CustomPropertyDetails,
	CustomPropertyDto,
	CustomPropertyRuleSetDto,
	PagedResultOfCustomPropertyRuleSetDto,
	PagedResultOfCustomPropertySummary,
	PagedResultOfRuleSetSummary,
	UpdateCustomPropertyRequest,
} from '../api/backstage.schemas'
import type { EntityConfig, ListQueryParams } from './entity-config.types'
import {
	defineMutationEndpoint,
	defineQueryEndpoint,
} from './entity-config.types'

// #endregion

// #region Entity Endpoints
const customPropertiesEntityEndpoints = {
	create: defineMutationEndpoint<
		CustomPropertyDto,
		CreateCustomPropertyRequest,
		undefined
	>({
		key: 'customProperties.create',
		method: 'POST',
		path: '/admin/customproperties',
		mutationOptions: () => ({
			mutationFn: (variables: { data: CreateCustomPropertyRequest }) =>
				customPropertiesCreate(variables.data),
		}),
		invalidates: [(_context) => getCustomPropertiesListQueryKey()],
	}),
	delete: defineMutationEndpoint<void, undefined, { customPropertyId: number }>(
		{
			key: 'customProperties.delete',
			method: 'DELETE',
			path: '/admin/customproperties/{customPropertyId}',
			pathParams: ['customPropertyId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					customPropertyId: number | string | null | undefined
				}) => customPropertiesDelete(variables.customPropertyId),
			}),
			invalidates: [(_context) => getCustomPropertiesListQueryKey()],
		},
	),
	detail: defineQueryEndpoint<
		CustomPropertyDetails,
		{ customPropertyId: number },
		undefined
	>({
		key: 'customProperties.detail',
		method: 'GET',
		path: '/admin/customproperties/{customPropertyId}',
		pathParams: ['customPropertyId'] as const,
		queryKey: (args) =>
			getCustomPropertiesDetailQueryKey(args?.pathParams?.customPropertyId),
		queryOptions: (args) =>
			getCustomPropertiesDetailQueryOptions(args?.pathParams?.customPropertyId),
	}),
	list: defineQueryEndpoint<
		PagedResultOfCustomPropertySummary,
		undefined,
		ListQueryParams
	>({
		key: 'customProperties.list',
		method: 'GET',
		path: '/admin/customproperties',
		queryKey: (args) => getCustomPropertiesListQueryKey(args?.queryParams),
		queryOptions: (args) =>
			getCustomPropertiesListQueryOptions(args?.queryParams),
	}),
	update: defineMutationEndpoint<
		CustomPropertyDto,
		UpdateCustomPropertyRequest,
		{ customPropertyId: number }
	>({
		key: 'customProperties.update',
		method: 'PUT',
		path: '/admin/customproperties/{customPropertyId}',
		pathParams: ['customPropertyId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				customPropertyId: number | string | null | undefined
				data: UpdateCustomPropertyRequest
			}) => customPropertiesUpdate(variables.customPropertyId, variables.data),
		}),
		invalidates: [
			(_context) => getCustomPropertiesListQueryKey(),
			(context) =>
				getCustomPropertiesDetailQueryKey(context.entityId ?? context.ownerId),
		],
	}),
} as const
// #endregion

// #region Relations
// #region Relation: RuleSets
// Target Entity: ruleSets
const customPropertiesRuleSetsRelation = {
	key: 'ruleSets',
	targetEntity: 'ruleSets',
	endpoints: {
		assign: defineMutationEndpoint<
			CustomPropertyRuleSetDto[],
			(number | string)[],
			{ customPropertyId: number }
		>({
			key: 'customProperties.relations.ruleSets.assign',
			method: 'POST',
			path: '/admin/customproperties/{customPropertyId}/rulesets',
			pathParams: ['customPropertyId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					customPropertyId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					customPropertiesRelationsRuleSetsAssign(
						variables.customPropertyId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getCustomPropertiesRelationsRuleSetsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getCustomPropertiesRelationsRuleSetsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getCustomPropertiesListQueryKey(),
				(_context) => getRuleSetsListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfCustomPropertyRuleSetDto,
			{ customPropertyId: number },
			ListQueryParams
		>({
			key: 'customProperties.relations.ruleSets.assigned',
			method: 'GET',
			path: '/admin/customproperties/{customPropertyId}/rulesets',
			pathParams: ['customPropertyId'] as const,
			queryKey: (args) =>
				getCustomPropertiesRelationsRuleSetsAssignedQueryKey(
					args?.pathParams?.customPropertyId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getCustomPropertiesRelationsRuleSetsAssignedQueryOptions(
					args?.pathParams?.customPropertyId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfRuleSetSummary,
			{ customPropertyId: number },
			ListQueryParams
		>({
			key: 'customProperties.relations.ruleSets.available',
			method: 'GET',
			path: '/admin/customproperties/{customPropertyId}/assignable-rulesets',
			pathParams: ['customPropertyId'] as const,
			queryKey: (args) =>
				getCustomPropertiesRelationsRuleSetsAvailableQueryKey(
					args?.pathParams?.customPropertyId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getCustomPropertiesRelationsRuleSetsAvailableQueryOptions(
					args?.pathParams?.customPropertyId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ customPropertyId: number }
		>({
			key: 'customProperties.relations.ruleSets.unassign',
			method: 'DELETE',
			path: '/admin/customproperties/{customPropertyId}/rulesets',
			pathParams: ['customPropertyId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					customPropertyId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					customPropertiesRelationsRuleSetsUnassign(
						variables.customPropertyId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getCustomPropertiesRelationsRuleSetsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getCustomPropertiesRelationsRuleSetsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getCustomPropertiesListQueryKey(),
				(_context) => getRuleSetsListQueryKey(),
			],
		}),
	},
} as const
// #endregion
// #endregion

// #region Client Endpoints
const customPropertiesClientEndpoints = {
	get: defineQueryEndpoint<CustomPropertyDto[], undefined, undefined>({
		key: 'customProperties.client.get',
		method: 'GET',
		path: '/api/customproperties',
		queryKey: (_args) => getCustomPropertiesClientGetQueryKey(),
		queryOptions: (_args) => getCustomPropertiesClientGetQueryOptions(),
	}),
	post: defineMutationEndpoint<
		CustomPropertyDto[],
		CreateCustomPropertiesRequest,
		undefined
	>({
		key: 'customProperties.client.post',
		method: 'POST',
		path: '/api/customproperties',
		mutationOptions: () => ({
			mutationFn: (variables: { data: CreateCustomPropertiesRequest }) =>
				customPropertiesClientPost(variables.data),
		}),
	}),
} as const
// #endregion

// #region Entity Config Export
export const customPropertiesEntityConfig = {
	key: 'customProperties',
	kind: 'entity',
	entity: {
		idField: 'id',
		idParam: 'customPropertyId',
		routePath: '/customProperties',
	},
	endpoints: customPropertiesEntityEndpoints,
	relations: {
		ruleSets: customPropertiesRuleSetsRelation,
	},
	client: customPropertiesClientEndpoints,
} as const satisfies EntityConfig<'customProperties'>
// #endregion
